import { InlineMath } from "react-katex";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

const symmetries = [
  { key: "OX", reflect: ([x, y]) => [x, -y] },
  { key: "OY", reflect: ([x, y]) => [-x, y] },
  { key: "origin", reflect: ([x, y]) => [-x, -y] },
];

export function generate() {
  let x1, y1, x2, y2;

  do {
    x1 = Math.floor(Math.random() * 11) - 5;
    y1 = Math.floor(Math.random() * 11) - 5;
    x2 = Math.floor(Math.random() * 11) - 5;
    y2 = Math.floor(Math.random() * 11) - 5;
  } while (x1 === x2 && y1 === y2);

  const symmetry = symmetries[Math.floor(Math.random() * symmetries.length)];
  const A = [x1, y1];
  const B = [x2, y2];
  const Ar = symmetry.reflect(A);
  const Br = symmetry.reflect(B);

  return {
    type: "SymmetrySegmentTask",
    data: {
      segment: [A, B],
      symmetryKey: symmetry.key,
    },
    answer: [Ar, Br],
  };
}

export function renderTask(task) {
  const [[x1, y1], [x2, y2]] = task.data.segment;
  const symmetryKey = task.data.symmetryKey;

  const labelText = {
    OX: "OX",
    OY: "OY",
    origin: "środka układu",
  }[symmetryKey];

  return (
    <p>
      Wyznacz obraz odcinka <InlineMath math={'AB'} /> o końcach{" "}
      <InlineMath math={`A = (${x1}, ${y1}),\\ B = (${x2}, ${y2})`} /> względem{" "}
      {symmetryKey === "origin" ? (
        <strong>{labelText}</strong>
      ) : (
        <InlineMath math={labelText} />
      )}
    </p>
  );
}

export function renderAnswer(task) {
  const [[x1, y1], [x2, y2]] = task.answer;

  return (
    <span>
      <InlineMath math={`A' = (${x1},\\ ${y1})`} />,{" "}
      <InlineMath math={`B' = (${x2},\\ ${y2})`} />
    </span>
  );
}

export function renderExplanation({ data, answer }) {
  const [[x1, y1], [x2, y2]] = data.segment;
  const [[x1r, y1r], [x2r, y2r]] = answer;

  const symmetryKey = data.symmetryKey;

  const explanation = {
    OX: (
      <>
        Odbicie względem osi <InlineMath math={"OX"} /> zmienia znak współrzędnej{" "}
        <InlineMath math={"y"} />.
      </>
    ),
    OY: (
      <>
        Odbicie względem osi <InlineMath math={"OY"} /> zmienia znak współrzędnej{" "}
        <InlineMath math={"x"} />.
      </>
    ),
    origin: (
      <>
        Odbicie względem <strong>środka układu</strong> zmienia znaki obu
        współrzędnych <InlineMath math={"x"} /> i <InlineMath math={"y"} />.
      </>
    ),
  }[symmetryKey];

  return (
    <div style={{ marginBottom: "32px" }}>
      <p>{explanation}</p>

      {/* Bez pionowego scrolla w środku: tylko poziomy, jeśli ekran zbyt wąski */}
      <div style={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
        <div style={{ minWidth: 520, height: 430, overflowY: "hidden" }}>
          <GraphViewer
            points={{
              A: { x: x1, y: y1 },
              B: { x: x2, y: y2 },
              "A'": { x: x1r, y: y1r },
              "B'": { x: x2r, y: y2r },
            }}
            segments={[
              { p1: "A", p2: "B" },
              { p1: "A'", p2: "B'" },
            ]}
          />
        </div>
      </div>
    </div>
  );
}

/** Input: 2× FractionInput dla A' + 2× FractionInput dla B' */
export function renderInput(_, userAnswer, setUserAnswer, disabled) {
  const ua = userAnswer ?? {};

  const setField = (key) => (val) => setUserAnswer({ ...ua, [key]: val });

  return (
    <div className="space-y-3">
      <div>
        <div style={{ marginBottom: 6 }}>
          <InlineMath math={"A'"} />:
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span>
              <InlineMath math={"x_{A'} ="} />
            </span>
            <FractionInput
              value={ua?.ax || ""}
              onChange={setField("ax")}
              disabled={disabled}
              label={"x_{A'}"}
            />
          </div>
          <div className="flex items-center gap-2">
            <span>
              <InlineMath math={"y_{A'} ="} />
            </span>
            <FractionInput
              value={ua?.ay || ""}
              onChange={setField("ay")}
              disabled={disabled}
              label={"y_{A'}"}
            />
          </div>
        </div>
      </div>

      <div>
        <div style={{ marginBottom: 6 }}>
          <InlineMath math={"B'"} />:
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span>
              <InlineMath math={"x_{B'} ="} />
            </span>
            <FractionInput
              value={ua?.bx || ""}
              onChange={setField("bx")}
              disabled={disabled}
              label={"x_{B'}"}
            />
          </div>
          <div className="flex items-center gap-2">
            <span>
              <InlineMath math={"y_{B'} ="} />
            </span>
            <FractionInput
              value={ua?.by || ""}
              onChange={setField("by")}
              disabled={disabled}
              label={"y_{B'}"}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 2 pkt: 1 pkt za A', 1 pkt za B'
 * Zwracamy obiekt { isCorrect, score, maxScore, parts } (jak w innych zadaniach punktowanych częściowo).
 */
export function checkAnswer(task, userInput) {
  const [[x1r, y1r], [x2r, y2r]] = task.answer;

  const ax = userInput?.ax?.parsed;
  const ay = userInput?.ay?.parsed;
  const bx = userInput?.bx?.parsed;
  const by = userInput?.by?.parsed;

  const aOk = ax === x1r && ay === y1r;
  const bOk = bx === x2r && by === y2r;

  const score = (aOk ? 1 : 0) + (bOk ? 1 : 0);

  return {
    isCorrect: score === 2,
    score,
    maxScore: 2,
    parts: { "A'": aOk, "B'": bOk },
  };
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return <i>(brak odpowiedzi)</i>;

  const hasA = userAnswer?.ax?.raw && userAnswer?.ay?.raw;
  const hasB = userAnswer?.bx?.raw && userAnswer?.by?.raw;

  if (!hasA && !hasB) return <i>(brak odpowiedzi)</i>;

  const ax = hasA ? formatLatexPreview(userAnswer.ax.raw, "x_{A'}") : null;
  const ay = hasA ? formatLatexPreview(userAnswer.ay.raw, "y_{A'}") : null;

  const bx = hasB ? formatLatexPreview(userAnswer.bx.raw, "x_{B'}") : null;
  const by = hasB ? formatLatexPreview(userAnswer.by.raw, "y_{B'}") : null;

  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <div style={{ marginBottom: 8 }}>
        <strong>A′</strong>
        {hasA ? (
          <>
            <div><InlineMath math={ax} /></div>
            <div style={{ marginTop: "0.5rem" }}><InlineMath math={ay} /></div>
          </>
        ) : (
          <div><i>(brak)</i></div>
        )}
      </div>

      <div>
        <strong>B′</strong>
        {hasB ? (
          <>
            <div><InlineMath math={bx} /></div>
            <div style={{ marginTop: "0.5rem" }}><InlineMath math={by} /></div>
          </>
        ) : (
          <div><i>(brak)</i></div>
        )}
      </div>
    </div>
  );
}
